import { useEffect, useState } from 'react';

import CommonImage from '@/components/CommonImage';
import {
	useLazyGetArtworkArtboardsQuery,
	useRemoveAssetsImageMutation
} from '@/features/assets/api/assetsSlice';
import { toastError } from '@/components/Toast';
import LoaderComponent from '@/components/LoaderComponent';
import useMultipartFileUpload from '@/hooks/useMultipartFileUpload';
import { useRemoveDocMutation } from '@/api/commonApis';
import { getDocType, getFileType } from '@/features/assets/utils';

const UploadArtwork = ({
	onSuccessImageUpload,
	onSuccessFileUpload,
	artWorkResponse,
	onUploadedFileName,
	file,
	setFile,
	setImage,
	setErrors,
	errors,
	setIsDisabledSubmit,
	artworkArtboardResponseList
}) => {
	const [imageDownloadUrl, setImageDownloadUrl] = useState('');
	const [fileDownloadUrl, setFileDownloadUrl] = useState('');
	const [fileId, setFileId] = useState(null);
	const [imageId, setImageId] = useState(null);
	const [artworkList, setArtworkList] = useState([]);

	const [removeAssetsImage, { isLoading: imageDeleting }] = useRemoveAssetsImageMutation();
	const [getArtworkArtboards] = useLazyGetArtworkArtboardsQuery();
	const [removeDoc] = useRemoveDocMutation();

	const { uploadProgress, isUploading, uploadFile } = useMultipartFileUpload({
		onSuccess: (result) => {
			if (result.documentType === 'ARTWORK_FRONT_IMAGE') {
				setImageDownloadUrl(result.downloadUrl);
				setImage(result);
				setImageId(result.documentId);
				onSuccessImageUpload(result.documentId);
			} else if (getDocType(result?.documentType)) {
				setFileDownloadUrl(result.downloadUrl);
				onSuccessFileUpload(result.documentId);
				setFileId(result.documentId);
				onUploadedFileName(result?.file?.name);
				getArtworkArtboards({ fileName: result?.file?.name?.split('.')[0] })
					.unwrap()
					.then((item) => {
						setArtworkList(item);
					});
			}
		},
		onError: (error) => {
			setErrors((prev) => ({
				...prev,
				file: error?.data?.message || 'Upload failed'
			}));
		}
	});

	useEffect(() => {
		if (artWorkResponse?.documentResponseList?.length > 0) {
			artWorkResponse?.documentResponseList?.forEach((item) => {
				if (getDocType(item?.docType)) {
					setFileDownloadUrl(item?.docUrl);
					setFile(item);
					setFileId(item?.id);
				} else {
					setImageDownloadUrl(item?.docUrl);
					setImage(item);
					setImageId(item?.id);
				}
			});
		}
	}, [artWorkResponse?.id]);

	useEffect(() => {
		if (artworkArtboardResponseList?.length > 0) {
			setArtworkList(artworkArtboardResponseList);
		}
		return () => {
			setArtworkList([]);
		};
	}, [artWorkResponse?.id]);

	const handleFileChange = async (e) => {
		const selectedFile = e.target.files[0];
		if (!selectedFile) return;

		setErrors((prev) => ({
			...prev,
			image: '',
			file: ''
		}));
		setFile(selectedFile);

		await uploadFile(selectedFile, getFileType(selectedFile?.type));

		e.target.value = '';
	};

	const handleImageUpload = async (e) => {
		const selectedFile = e.target.files[0];
		if (!selectedFile) return;
		setImage(selectedFile);
		await uploadFile(selectedFile, 'ARTWORK_FRONT_IMAGE');
	};

	const handleRemoveDoc = (docId, docType) => {
		const handleSuccess = () => {
			if (docType === 'IMAGE') {
				setImage(null);
				setImageDownloadUrl('');
				setImageId(null);
				onSuccessImageUpload(null);
			} else if (docType === 'FILE') {
				setArtworkList([]);
				setFileId(null);
				setFileDownloadUrl('');
				setFile(null);
				onSuccessFileUpload(null);
			}
		};

		const handleError = (error) => {
			toastError(error?.data?.message);
		};

		const apiCall = artWorkResponse?.id
			? removeAssetsImage({ docId, artworkId: artWorkResponse.id })
			: removeDoc({ docId });

		apiCall.unwrap().then(handleSuccess).catch(handleError);
	};

	useEffect(() => {
		if (isUploading) {
			setIsDisabledSubmit(true);
		} else {
			setIsDisabledSubmit(false);
		}
	}, [isUploading]);

	return (
		<LoaderComponent loading={imageDeleting}>
			<div className="dashboard-container bg-white py-0">
				<div className="presentations-tab px-0 pt-0">
					<div className="presentations-upload-image-container">
						<div class="one-forth">
							<>
								{!imageDownloadUrl ? (
									<div class="file-uploader upload-design-image ">
										<div
											class={`file-uploader small upload-design-image mb-4 mb-sm-0 ${errors?.image ? 'image-error' : ''}`}
										>
											<label htmlFor="image-upload" className="drag-upload"></label>
											<input
												type="file"
												className="file-upload d-none"
												name="file"
												id="image-upload"
												onChange={handleImageUpload}
												onClick={(e) => (e.target.value = '')}
												accept=".jpg, .jpeg, .png, .gif, .bmp, .tiff, .webp"
												disabled={isUploading}
											/>
										</div>
									</div>
								) : (
									<div class="uploaded-file">
										<CommonImage
											onClick={() => handleRemoveDoc(imageId, 'IMAGE')}
											src="/icons/delete_gray.svg"
											class="crop delete"
											alt="delete"
											width={20}
											height={20}
										/>

										<div class="uploaded-image">
											<CommonImage
												src={imageDownloadUrl}
												alt="Upload"
												width={100}
												height={100}
												isStatic={false}
											/>
										</div>
									</div>
								)}
							</>
						</div>
					</div>
					{isUploading && (
						<div className="progress-info task-progress-info mt-2">
							<div className="progress progress-square">
								<div
									className="progress-bar"
									style={{ width: `${uploadProgress}%`, transition: 'width 0.3s ease' }}
								></div>
							</div>
						</div>
					)}

					<div className="upload-presentaion-and-psd add-pdf-files d-flex align-items-center py-3 pl-0">
						<div className="upload-psd-btn">
							<label
								className={`button outline cursor-pointer size36 ${errors?.file ? 'file-upload-error' : ''} ${isUploading || fileDownloadUrl ? 'disable' : ''}`}
								htmlFor="file-upload"
							>
								<span>
									<CommonImage src="/icons/upload-dark.svg" alt="Upload po" />
								</span>
								{isUploading ? 'Uploading...' : 'Upload file'}
							</label>
							<input
								type="file"
								className="file-upload d-none"
								name="file"
								id="file-upload"
								onChange={handleFileChange}
								onClick={(e) => (e.target.value = '')}
								accept=".ai, .psd"
								disabled={isUploading}
							/>
						</div>

						{/* File Info and Progress */}
						{fileDownloadUrl && (
							<div className="all-uploaded-psd gap-2 d-flex align-items-center flex-wrap">
								<div className="single-upload">
									<p className="regular-14 gray_dark_01 cursor-pointer">
										{file?.name?.slice(0, 5)?.concat(`...${file?.name.slice(-5)}`)}
									</p>
									<div className="more-dropdown dropdown">
										<button
											className="button text-only"
											type="button"
											id="moreInfo"
											data-toggle="dropdown"
											aria-expanded="false"
										>
											<CommonImage
												src="/icons/more_icon.svg"
												alt="more"
												className="mr-1"
											/>
										</button>

										<ul
											className="dropdown-menu dropdown-menu-right shadow-lg"
											role="menu"
											aria-labelledby="moreInfo"
											x-placement="bottom-end"
										>
											<li>
												<span onClick={() => window.open(fileDownloadUrl, '_blank')}>
													Download
												</span>
											</li>
											<li>
												<span
													className="red"
													onClick={() => handleRemoveDoc(fileId, 'FILE')}
												>
													Delete
												</span>
											</li>
										</ul>
									</div>
								</div>
							</div>
						)}
					</div>
					<div className="all-artwork-file d-flex">
						<div className="all-file-title pr-2">
							<p className="regular-2 gray_dark_01">Artwork No:</p>
						</div>
						<div className="all-file d-flex flex-wrap align-items-center gap-2">
							{artworkList?.map((item) => (
								<span key={item?.id} className="chip-square ">
									{item?.artboardReferenceNumber}
								</span>
							))}
						</div>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default UploadArtwork;
